import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaSave, FaPlus, FaInfoCircle, FaLightbulb } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const SurveyForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    survey_type: 'organization',
    organization_id: user?.role === 2 ? user.organization_id : '',
    course_id: '',
    image: null,
    status: 1
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchOrganizations();
    fetchCourses();
    
    if (isEditMode) {
      fetchSurvey();
    }
  }, [id]);

  const fetchSurvey = async () => {
    try {
      const response = await api.surveys.getById(id);
      const survey = response.data.data;
      
      setFormData({
        title: survey.title || '',
        description: survey.description || '',
        survey_type: survey.survey_type || 'organization',
        organization_id: survey.organization_id || '',
        course_id: survey.course_id || '',
        image: null,
        status: survey.status
      });

      if (survey.image_path) {
        setImagePreview(`${process.env.REACT_APP_API_BASE_URL?.replace('/api', '')}${survey.image_path}`);
      }
    } catch (error) {
      console.error('Error fetching survey:', error);
      toast.error('Failed to load survey');
      navigate('/admin/surveys');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.organizations.getAll();
      setOrganizations(response.data.data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations([]);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await api.courses.getAll(true);
      setCourses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'survey_type' && value === 'global') {
      setFormData(prev => ({
        ...prev,
        organization_id: ''
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, GIF, WEBP)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }

      setFormData(prev => ({
        ...prev,
        image: file
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Survey title is required';
    }

    if (!formData.survey_type) {
      newErrors.survey_type = 'Survey type is required';
    }

    if (formData.survey_type === 'organization' && !formData.organization_id) {
      newErrors.organization_id = 'Organization is required for organization-level surveys';
    }

    if (user?.role === 2) {
      if (formData.survey_type === 'global') {
        newErrors.survey_type = 'You can only create organization-level surveys';
      }
      if (formData.organization_id && formData.organization_id !== user.organization_id.toString()) {
        newErrors.organization_id = 'You can only create surveys for your organization';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setSubmitting(true);

      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('survey_type', formData.survey_type);
      
      if (formData.survey_type === 'organization') {
        submitData.append('organization_id', formData.organization_id);
      }
      
      if (formData.course_id) {
        submitData.append('course_id', formData.course_id);
      }
      
      if (formData.image) {
        submitData.append('image', formData.image);
      }
      
      submitData.append('status', formData.status);

      if (isEditMode) {
        await api.surveys.update(id, submitData);
        toast.success('Survey updated successfully');
      } else {
        await api.surveys.create(submitData);
        toast.success('Survey created successfully');
      }

      navigate('/admin/surveys');
    } catch (error) {
      console.error('Error saving survey:', error);
      toast.error(error.response?.data?.message || 'Failed to save survey');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96 p-8">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mb-4"></div>
        <p className="text-purple-700 font-medium">Loading survey...</p>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/surveys')}
            className="p-3 rounded-xl bg-white border border-purple-200 text-purple-600 hover:bg-purple-50 hover:border-purple-400 transition-all shadow-sm"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
              {isEditMode ? 'Edit Survey' : 'Create New Survey'}
            </h2>
            <p className="text-gray-600 mt-1">
              {isEditMode ? 'Update survey details' : 'Create a new survey template'}
            </p>
          </div>
        </div>
      </div>
      

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Survey Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full rounded-lg border ${errors.title ? 'border-red-500' : 'border-purple-200'} focus:ring-purple-500 focus:border-purple-500 text-sm h-12 px-4`}
                  placeholder="Enter survey title"
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  rows={5}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-purple-200 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none p-4"
                  placeholder="Enter survey description (optional)"
                />
              </div>

              {/* Survey Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Survey Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="survey_type"
                  value={formData.survey_type}
                  onChange={handleChange}
                  disabled={user?.role === 2}
                  className={`w-full rounded-lg border ${errors.survey_type ? 'border-red-500' : 'border-purple-200'} focus:ring-purple-500 focus:border-purple-500 text-sm h-12 px-4 disabled:bg-gray-100`}
                >
                  <option value="organization">Organization Level</option>
                  {user?.role === 0 && <option value="global">Global (All Users)</option>}
                </select>
                {errors.survey_type && <p className="text-red-500 text-xs mt-1">{errors.survey_type}</p>}
                <p className="text-xs text-gray-500 mt-2">
                  {formData.survey_type === 'global' 
                    ? 'This survey will be visible to all users across all organizations' 
                    : 'This survey will be visible only to users in the selected organization'}
                </p>
              </div>

              {/* Organization */}
              {formData.survey_type === 'organization' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Organization <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="organization_id"
                    value={formData.organization_id}
                    onChange={handleChange}
                    disabled={user?.role === 2}
                    className={`w-full rounded-lg border ${errors.organization_id ? 'border-red-500' : 'border-purple-200'} focus:ring-purple-500 focus:border-purple-500 text-sm h-12 px-4 disabled:bg-gray-100`}
                  >
                    <option value="">Select Organization</option>
                    {organizations
                      .filter(org => !org.deleted && org.status === 1)
                      .map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                  </select>
                  {errors.organization_id && <p className="text-red-500 text-xs mt-1">{errors.organization_id}</p>}
                </div>
              )}

              {/* Course */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Link to Course (Optional)
                </label>
                <select
                  name="course_id"
                  value={formData.course_id}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-purple-200 focus:ring-purple-500 focus:border-purple-500 text-sm h-12 px-4"
                >
                  <option value="">No Course</option>
                  {courses
                    .filter(course => !course.deleted && course.status === 1)
                    .map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">Optionally link this survey to a specific course</p>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Survey Image
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageChange}
                  className="w-full rounded-lg border border-purple-200 focus:ring-purple-500 text-sm file:mr-4 file:py-2.5 file:px-5 file:rounded-lg file:border-0 file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                />
                <p className="text-xs text-gray-500 mt-1">Max 5MB • JPEG, PNG, GIF, WEBP</p>
                {imagePreview && (
                  <div className="mt-4">
                    <img 
                      src={imagePreview} 
                      alt="Survey preview" 
                      className="max-w-full max-h-64 rounded-xl shadow-lg border-4 border-white"
                    />
                  </div>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-purple-200 focus:ring-purple-500 focus:border-purple-500 text-sm h-12 px-4"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
                <p className="text-xs text-gray-500 mt-2">Only active surveys will be visible to users</p>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-3 py-4 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-70"
                >
                  {submitting ? (
                    <>Saving...</>
                  ) : (
                    <>
                      {isEditMode ? <FaSave className="w-5 h-5" /> : <FaPlus className="w-5 h-5" />}
                      {isEditMode ? 'Update Survey' : 'Create Survey'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/admin/surveys')}
                  disabled={submitting}
                  className="flex-1 py-4 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar Guide */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-purple-100 text-purple-600">
                <FaInfoCircle className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Quick Guide</h3>
            </div>

            <div className="space-y-6 text-sm text-gray-600">
              <div>
                <h4 className="font-bold text-gray-800 mb-2">Survey Types:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">•</span>
                    <div>
                      <strong>Organization Level:</strong> Visible only to users within the selected organization
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">•</span>
                    <div>
                      <strong>Global:</strong> Visible to all users across all organizations (Super Admin only)
                    </div>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-gray-800 mb-2">Next Steps:</h4>
                <ol className="space-y-2 list-decimal list-inside">
                  <li>Create the survey template</li>
                  <li>Add questions to the survey</li>
                  <li>Set the survey status to Active</li>
                  <li>Users can then respond to the survey</li>
                </ol>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-200">
                <div className="flex items-start gap-3">
                  <FaLightbulb className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-700 font-medium">
                      After creating the survey, you'll be able to add questions from the survey list.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyForm;